import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useSubCalendars } from '../context/SubCalendarContext';
import { useAnchoredPanel } from '../hooks/useAnchoredPanel';
import EventIcon, { ICONS, iconNames } from './EventIcon';

/**
 * The way in to a label's agenda that does not depend on today.
 *
 * Until now a label could only be reached by tapping one on an event, which
 * means the question "when do I next have the gym?" could only be asked on a
 * day that already had the gym on it. The one day you most need the answer —
 * an empty screen — was the one day nothing was there to tap.
 *
 * Built like the language menu, and each entry drawn exactly as the same thing
 * is drawn on an event: a label is its own name in its own colour, a shared
 * calendar is its name, a drawing is the drawing. Silvia asked for them to be
 * "perfettamente identici e prontamente riconoscibili", and she is right that
 * a second visual language for the same objects is a second thing to learn —
 * a coloured dot beside a name is a legend, and a legend has to be read.
 */
const W = {
  it: { open: 'Etichette e calendari', labels: 'Etichette', shared: 'Calendari condivisi', icons: 'Disegni' },
  en: { open: 'Labels and calendars', labels: 'Labels', shared: 'Shared calendars', icons: 'Drawings' },
  es: { open: 'Etiquetas y calendarios', labels: 'Etiquetas', shared: 'Calendarios compartidos', icons: 'Dibujos' },
  de: { open: 'Labels und Kalender', labels: 'Labels', shared: 'Geteilte Kalender', icons: 'Symbole' },
  fr: { open: 'Étiquettes et agendas', labels: 'Étiquettes', shared: 'Agendas partagés', icons: 'Dessins' },
  nl: { open: 'Labels en agenda’s', labels: 'Labels', shared: 'Gedeelde agenda’s', icons: 'Tekeningen' },
  pt: { open: 'Etiquetas e calendários', labels: 'Etiquetas', shared: 'Calendários partilhados', icons: 'Desenhos' },
  ru: { open: 'Метки и календари', labels: 'Метки', shared: 'Общие календари', icons: 'Рисунки' },
  pl: { open: 'Etykiety i kalendarze', labels: 'Etykiety', shared: 'Kalendarze współdzielone', icons: 'Rysunki' },
  ro: { open: 'Etichete și calendare', labels: 'Etichete', shared: 'Calendare partajate', icons: 'Desene' },
  zh: { open: '标签和日历', labels: '标签', shared: '共享日历', icons: '图标' },
  ja: { open: 'ラベルとカレンダー', labels: 'ラベル', shared: '共有カレンダー', icons: 'イラスト' },
  hi: { open: 'लेबल और कैलेंडर', labels: 'लेबल', shared: 'साझा कैलेंडर', icons: 'चित्र' },
  ko: { open: '라벨과 캘린더', labels: '라벨', shared: '공유 캘린더', icons: '그림' },
  ar: { open: 'العلامات والتقاويم', labels: 'العلامات', shared: 'التقاويم المشتركة', icons: 'الرسوم' },
};

// The same card as the language menu: same height, same corners, same border.
const CARD = {
  display: 'flex', alignItems: 'center', gap: 8,
  minHeight: 46, padding: '8px 10px', width: '100%',
  fontFamily: 'inherit', fontSize: '0.9rem',
  textAlign: 'start', cursor: 'pointer',
  borderRadius: 10,
  background: 'var(--color-card)',
  border: '1.5px solid var(--color-border)',
};

// A heading between the groups, quiet enough not to compete with the names.
function Head({ children }) {
  return (
    <div style={{
      fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em',
      color: 'var(--color-text-muted)', padding: '10px 2px 6px',
    }}>{children}</div>
  );
}

export default function LabelsMenu() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subCalendars } = useSubCalendars();
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const boxRef = useRef(null);

  const lang = String(user?.language || 'it');
  const base = lang.split('-')[0];
  const t = W[base] || W.en;
  const names = iconNames(lang);
  const panelStyle = useAnchoredPanel(open, btnRef, panelRef);

  useEffect(() => {
    let alive = true;
    apiClient.get('/groups')
      .then(r => { if (alive) setGroups(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});   // a menu is not worth an error message
    return () => { alive = false; };
  }, []);

  // Tap anywhere else, or press Escape, and it closes.
  useEffect(() => {
    if (!open) return undefined;
    function away(e) {
      if (boxRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function key(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const labels = (subCalendars || []).filter(sc => sc && sc.id);

  function go(path) { setOpen(false); navigate(path); }

  return (
    <div ref={boxRef} style={{ display: 'inline-flex' }}>
      {/* The same size as the planner and the speaker beside it. They are one
          row of controls and one of them being smaller reads as an accident. */}
      <button
        ref={btnRef}
        type="button"
        className="tts-btn"
        onClick={() => setOpen(o => !o)}
        aria-label={t.open}
        title={t.open}
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"
             fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.6 13.4 12.2 5A2 2 0 0 0 10.8 4.4H5a1 1 0 0 0-1 1v5.8a2 2 0 0 0 .6 1.4l8.4 8.4a2 2 0 0 0 2.8 0l4.8-4.8a2 2 0 0 0 0-2.8Z" />
          <circle cx="8" cy="8" r="1.3" fill="currentColor" stroke="none" />
        </svg>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          style={{
            zIndex: 60, width: 320, overflowY: 'auto',
            background: 'var(--color-card)', border: '1px solid var(--color-border)',
            borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: '4px 10px 10px',
            textAlign: 'start',
            // Off the screen twice, in two different places, for the same
            // reason. See hooks/useAnchoredPanel.js.
            ...(panelStyle || { position: 'fixed', top: -9999, left: -9999 }),
          }}
        >
          {labels.length > 0 && <Head>{t.labels}</Head>}
          {labels.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
              {labels.map(sc => (
                <button key={sc.id} type="button" role="menuitem" className="tap-grow"
                        onClick={() => go(`/label/${sc.id}`)}
                        style={{ ...CARD, '--tap-x': '4px' }}>
                  {sc.emoji ? <EventIcon icon={sc.emoji} size={20} style={{ flexShrink: 0 }} /> : null}
                  {/* The label's own name in the label's own colour — the very
                      same span an event carries under its title. */}
                  <span className="event-label" style={{ color: sc.color || 'var(--color-primary)' }}>
                    {sc.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          {groups.length > 0 && <Head>{t.shared}</Head>}
          {groups.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
              {groups.map(g => (
                <button key={g.id} type="button" role="menuitem" className="tap-grow"
                        onClick={() => go(`/shared/${g.id}`)}
                        style={{ ...CARD, '--tap-x': '4px' }}>
                  <span className="event-label" style={{ color: 'var(--color-text-muted)' }}>
                    {g.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          <Head>{t.icons}</Head>
          {/* The drawings are the third filter — tap the cake and every
              birthday still to come is there. Only the drawing, no name: they
              were chosen to be read at a glance, and an empty answer is a real
              answer to "have I got any birthdays coming up?". */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(46px, 1fr))', gap: 6 }}>
            {ICONS.map(ic => (
              <button key={ic} type="button" role="menuitem" className="tap-grow"
                      onClick={() => go(`/icon/${encodeURIComponent(ic)}`)}
                      aria-label={names[ic] || ic} title={names[ic] || ic}
                      style={{
                        ...CARD, '--tap-x': '2px',
                        justifyContent: 'center', minHeight: 46, padding: 4, gap: 0,
                      }}>
                <EventIcon icon={ic} size={26} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
